import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import { orderService } from '../services/orderService';
import { formatDateForInput, formatLocalTodayYmd, parseLocalDate } from '../utils/dateUtils';

function formatShippedMd(ymd) {
  if (!ymd || typeof ymd !== 'string') return '';
  const d = parseLocalDate(ymd);
  return d ? format(d, 'M/d') : '';
}

/** Due row card outline (blue); DUE status chip blue; SHIPPED status chip green (matches on-time shipment tones). */
const CAL_BLUE_BORDER = '#2196F3';
/** Light fill inside the oval order cards (Material blue 50). */
const CAL_BLUE_CARD_FILL = '#E3F2FD';
/** Slightly deeper blue on hover (blue 100). */
const CAL_BLUE_CARD_HOVER = '#BBDEFB';
const CAL_CUSTOMER_MAX_LEN = 9;

/** Due row order · customer text: readable size, wraps within the day cell. */
const CAL_DUE_ROW_TEXT_SX = {
  fontSize: '0.7rem',
  lineHeight: 1.35,
  fontWeight: 600,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  flex: '1 1 auto',
  minWidth: 0
};

/** Pill / oval outline on due-order cards (large radius on all corners). */
const CAL_DUE_ORDER_CARD_RADIUS = 9999;

const CAL_DUE_STATUS_CHIP_SX = {
  textTransform: 'uppercase',
  backgroundColor: '#64B5F6',
  color: '#0D47A1',
  fontWeight: 700,
  height: 'auto',
  minHeight: 22,
  flexShrink: 0,
  alignSelf: 'center',
  border: '1px solid #1E88E5',
  borderRadius: 9999,
  '& .MuiChip-label': {
    px: 0.55,
    py: 0.35,
    fontSize: '0.65rem',
    lineHeight: 1.25,
    whiteSpace: 'normal',
    textAlign: 'left'
  }
};

const CAL_SHIPPED_STATUS_CHIP_SX = {
  textTransform: 'uppercase',
  backgroundColor: '#C8E6C9',
  color: '#1B5E20',
  fontWeight: 700,
  height: 'auto',
  minHeight: 22,
  flexShrink: 0,
  alignSelf: 'center',
  border: '1px solid #2E7D32',
  borderRadius: 9999,
  '& .MuiChip-label': {
    px: 0.55,
    py: 0.35,
    fontSize: '0.65rem',
    lineHeight: 1.25,
    whiteSpace: 'normal',
    textAlign: 'left'
  }
};

/** Shipment chips: on-time (green) vs late (amber) — vivid fills and borders. */
const CAL_SHIPMENT_ON_TIME_SX = {
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: '#2E7D32',
  backgroundColor: '#C8E6C9',
  color: '#1B5E20',
  fontWeight: 600,
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    display: 'block',
    py: 0.25,
    px: 0.45,
    fontSize: '0.65rem',
    lineHeight: 1.25,
    textAlign: 'left'
  }
};

const CAL_SHIPMENT_LATE_SX = {
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: '#FF8F00',
  backgroundColor: '#FFE082',
  color: '#E65100',
  fontWeight: 600,
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    display: 'block',
    py: 0.25,
    px: 0.45,
    fontSize: '0.65rem',
    lineHeight: 1.25,
    textAlign: 'left'
  }
};

function orderIdLabel(row) {
  return (
    row.identifier ||
    (row.work_order_number != null ? String(row.work_order_number) : '') ||
    (row.id != null ? `#${row.id}` : '') ||
    (row.work_order_id != null ? `#${row.work_order_id}` : '')
  );
}

/** Prefer customer short name, then company name (calendar list text). */
function customerCalendarName(row) {
  const sn = (row.customer_short_name != null ? String(row.customer_short_name) : '').trim();
  if (sn) return sn;
  return (row.company_name || '').trim();
}

function truncateCalendarCustomer(name, maxLen = CAL_CUSTOMER_MAX_LEN) {
  const s = (name || '').trim();
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen);
}

function isShipmentLateVsDue(shipDateYmd, dueDateValue) {
  const due = formatDateForInput(dueDateValue);
  if (!due || !shipDateYmd) return false;
  return shipDateYmd > due;
}

function shipmentChipSx(row) {
  if (isShipmentLateVsDue(row.ship_date, row.due_date)) return CAL_SHIPMENT_LATE_SX;
  return CAL_SHIPMENT_ON_TIME_SX;
}

const OrdersCalendar = () => {
  const [anchorMonth, setAnchorMonth] = useState(() => startOfMonth(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dueRows, setDueRows] = useState([]);
  const [shippedRows, setShippedRows] = useState([]);
  const [showShipments, setShowShipments] = useState(true);
  const [showShippedOrders, setShowShippedOrders] = useState(true);
  const [showOrdersDue, setShowOrdersDue] = useState(true);

  const range = useMemo(() => {
    const from = format(startOfMonth(anchorMonth), 'yyyy-MM-dd');
    const to = format(endOfMonth(anchorMonth), 'yyyy-MM-dd');
    return { from, to };
  }, [anchorMonth]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await orderService.getCalendar(range);
      if (!res?.success || !res.data) {
        setError(res?.message || 'Failed to load calendar');
        setDueRows([]);
        setShippedRows([]);
        return;
      }
      setDueRows(res.data.due || []);
      setShippedRows(res.data.shipped || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || 'Failed to load calendar');
      setDueRows([]);
      setShippedRows([]);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const byDay = useMemo(() => {
    const map = new Map();
    const ensure = (key) => {
      if (!map.has(key)) map.set(key, { due: [], shipped: [] });
      return map.get(key);
    };

    for (const row of dueRows) {
      const key = formatDateForInput(row.due_date);
      if (!key) continue;
      ensure(key).due.push(row);
    }
    for (const row of shippedRows) {
      const key = row.ship_date;
      if (!key || typeof key !== 'string') continue;
      ensure(key).shipped.push(row);
    }
    return map;
  }, [dueRows, shippedRows]);

  const gridDays = useMemo(() => {
    const monthStart = startOfMonth(anchorMonth);
    const monthEnd = endOfMonth(anchorMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [anchorMonth]);

  const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const todayYmd = formatLocalTodayYmd();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" component="h1">
          Orders calendar
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Previous month">
            <IconButton size="small" onClick={() => setAnchorMonth((m) => addMonths(m, -1))} aria-label="Previous month">
              <ChevronLeft />
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle1" sx={{ minWidth: 160, textAlign: 'center' }}>
            {format(anchorMonth, 'MMMM yyyy')}
          </Typography>
          <Tooltip title="Next month">
            <IconButton size="small" onClick={() => setAnchorMonth((m) => addMonths(m, 1))} aria-label="Next month">
              <ChevronRight />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 1, overflow: 'visible' }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              mb: 1.5,
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ width: '100%', sm: { width: 'auto' }, mr: { sm: 1 } }}>
              Legend
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showShipments}
                  onChange={(e) => setShowShipments(e.target.checked)}
                />
              }
              label="Shipments"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showShippedOrders}
                  onChange={(e) => setShowShippedOrders(e.target.checked)}
                />
              }
              label="Shipped Orders"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showOrdersDue}
                  onChange={(e) => setShowOrdersDue(e.target.checked)}
                />
              }
              label="Orders Due"
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 0.5,
              minWidth: 560
            }}
          >
            {weekdayLabels.map((w) => (
              <Typography key={w} variant="caption" color="text.secondary" sx={{ fontWeight: 600, px: 0.5, py: 0.5 }}>
                {w}
              </Typography>
            ))}
            {gridDays.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const inMonth = isSameMonth(day, anchorMonth);
              const cell = byDay.get(key) || { due: [], shipped: [] };
              const isToday = key === todayYmd;

              return (
                <Box
                  key={key}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 0.5,
                    minHeight: 100,
                    p: 0.5,
                    bgcolor: isToday ? 'action.hover' : inMonth ? 'background.paper' : 'grey.50',
                    opacity: inMonth ? 1 : 0.65,
                    overflow: 'visible',
                    position: 'relative',
                    zIndex: 0
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: inMonth ? 600 : 400, display: 'block', mb: 0.25 }}
                  >
                    {format(day, 'd')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      alignItems: 'flex-start',
                      overflow: 'visible'
                    }}
                  >
                    {cell.due
                      .filter((row) => {
                        const hasShip = !!(row.first_ship_date && String(row.first_ship_date).trim());
                        if (hasShip) return showShippedOrders;
                        return showOrdersDue;
                      })
                      .map((row) => {
                        const oid = orderIdLabel(row);
                        const cust = customerCalendarName(row);
                        const line1 = cust ? `${oid} · ${cust}` : oid || 'Order';
                        const hasShip = !!(row.first_ship_date && String(row.first_ship_date).trim());
                        const shipYmd = hasShip ? String(row.first_ship_date).trim() : '';
                        const shipMd = shipYmd ? formatShippedMd(shipYmd) : '';
                        const dueYmd = formatDateForInput(row.due_date);
                        const dueMd = dueYmd ? formatShippedMd(dueYmd) : '';
                        const statusLabel = hasShip
                          ? shipMd
                            ? `SHIPPED ${shipMd}`
                            : 'SHIPPED'
                          : dueMd
                            ? `DUE ${dueMd}`
                            : 'DUE';
                        return (
                          <Box
                            key={`due-${row.id}`}
                            component={RouterLink}
                            to={`/work-orders/${row.id}`}
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              boxSizing: 'border-box',
                              border: 2,
                              borderColor: CAL_BLUE_BORDER,
                              borderRadius: CAL_DUE_ORDER_CARD_RADIUS,
                              bgcolor: CAL_BLUE_CARD_FILL,
                              px: 2.5,
                              py: 1.5,
                              textDecoration: 'none',
                              color: 'inherit',
                              display: 'flex',
                              flexDirection: 'row',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 0.75,
                              '&:hover': { bgcolor: CAL_BLUE_CARD_HOVER, zIndex: 2, position: 'relative' }
                            }}
                          >
                            <Typography component="span" variant="caption" sx={CAL_DUE_ROW_TEXT_SX}>
                              {line1}
                            </Typography>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={hasShip ? CAL_SHIPPED_STATUS_CHIP_SX : CAL_DUE_STATUS_CHIP_SX}
                            />
                          </Box>
                        );
                      })}
                    {showShipments &&
                      cell.shipped.map((row) => {
                        const oid = orderIdLabel(row);
                        const cust = truncateCalendarCustomer(customerCalendarName(row));
                        const line1 = cust ? `${oid} · ${cust}` : oid || 'Order';
                        const shipSx = shipmentChipSx(row);
                        return (
                          <Chip
                            key={`ship-${row.work_order_id}-${row.ship_date}-${row.packing_slip_id ?? ''}`}
                            component={RouterLink}
                            to={`/work-orders/${row.work_order_id}`}
                            label={line1}
                            size="small"
                            variant="outlined"
                            clickable
                            sx={{
                              maxWidth: '100%',
                              height: 'auto',
                              minHeight: 24,
                              borderRadius: 9999,
                              ...shipSx,
                              '& .MuiChip-label': {
                                ...shipSx['& .MuiChip-label']
                              }
                            }}
                          />
                        );
                      })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default OrdersCalendar;
